import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import DeviceList from '../components/DeviceList';
import { AuthContext } from '../context/AuthContext';
import { Button, OverlayTrigger, Tooltip, Modal, Row, Col } from 'react-bootstrap';
import { QuestionCircle } from 'react-bootstrap-icons';
import { DashboardSkeleton } from '../components/ui/Skeleton';

const Dashboard = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [deviceCount, setDeviceCount] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [lastActivity, setLastActivity] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitExceededData, setLimitExceededData] = useState(null);
  const hasShownLimitModal = useRef(false);

  const getDeviceLimit = (subTier) => {
    if (!subTier || subTier === 'free' || subTier === 'tinkerer') return 2;
    if (subTier === 'hobbyist') return 5;
    if (subTier === 'botanist') return 8;
    return 2;
  };

  const handleDevicesChange = useCallback((devices) => {
    setDeviceCount(devices.length);
    const now = Date.now();
    const online = devices.filter((d) => d.timestamp && now - new Date(d.timestamp).getTime() < 30000).length;
    setOnlineCount(online);
    const latest = devices.reduce((max, d) => {
      if (!d.timestamp) return max;
      const t = new Date(d.timestamp).getTime();
      return t > max ? t : max;
    }, 0);
    setLastActivity(latest ? new Date(latest) : null);
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/app');
      return;
    }

    const fetchData = async () => {
      try {
        const profileResponse = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserProfile(profileResponse.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const socket = io(process.env.REACT_APP_API_URL || '');
    socket.emit('authenticate', token);

    socket.on('deviceLimitExceeded', (data) => {
      if (!hasShownLimitModal.current) {
        setLimitExceededData(data);
        setShowLimitModal(true);
        hasShownLimitModal.current = true;
      }
    });

    return () => socket.disconnect();
  }, [token, navigate]);

  if (loading) return <DashboardSkeleton />;

  const deviceLimit = userProfile ? getDeviceLimit(userProfile.subTier) : 2;
  const hasReachedLimit = deviceCount >= deviceLimit;
  const usagePercent = deviceLimit > 0 ? Math.min(100, (deviceCount / deviceLimit) * 100) : 0;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <p className="mb-0 text-app-muted">
          Welcome back{user.firstName ? `, ${user.firstName}` : ''}!
        </p>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/app/devices/register')}
            disabled={hasReachedLimit}
          >
            Register Device
          </Button>
          {hasReachedLimit && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  Device limit reached. Your {userProfile?.subTier || 'free'} plan allows up to {deviceLimit} devices.
                </Tooltip>
              }
            >
              <QuestionCircle className="ms-1" style={{ cursor: 'pointer' }} />
            </OverlayTrigger>
          )}
        </div>
      </div>

      <Row className="mb-4 g-3">
        <Col md={4}>
          <div className="app-stat-card p-3">
            <div className="app-stat-card__label">Devices Online</div>
            <div className="app-stat-card__value">{onlineCount} / {deviceCount}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="app-stat-card p-3">
            <div className="app-stat-card__label">Plan Usage</div>
            <div className="app-stat-card__value">{deviceCount} / {deviceLimit}</div>
            <div className="tier-usage-bar">
              <div className="tier-usage-bar__fill" style={{ width: `${usagePercent}%` }} />
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="app-stat-card p-3">
            <div className="app-stat-card__label">Last Activity</div>
            <div className="app-stat-card__value" style={{ fontSize: '1.1rem' }}>
              {lastActivity ? lastActivity.toLocaleString() : '—'}
            </div>
          </div>
        </Col>
      </Row>

      <DeviceList
        onDevicesChange={handleDevicesChange}
        onRegisterDevice={() => navigate('/app/devices/register')}
        hasReachedLimit={hasReachedLimit}
      />

      <Modal
        show={showLimitModal}
        onHide={() => {
          setShowLimitModal(false);
          hasShownLimitModal.current = false;
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontFamily: 'var(--font-display)' }}>Device Limit Exceeded</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <QuestionCircle size={48} className="text-warning mb-3" />
            <h5>You have exceeded your device limit!</h5>
            <p className="mb-2">
              Your <strong>{limitExceededData?.subTier || 'free'}</strong> plan allows up to{' '}
              <strong>{limitExceededData?.limit || 2}</strong> devices.
            </p>
            <p className="mb-2">
              You currently have <strong>{limitExceededData?.current || 0}</strong> devices.
            </p>
            <p className="text-muted small">
              Sensor data from devices beyond your limit will not be saved to the server.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLimitModal(false)}>Close</Button>
          <Button variant="primary" onClick={() => navigate('/shop')}>Upgrade Plan</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Dashboard;